/**
 * 区间作业量与速率：行 = 作业，列 = 汇总 / 速率 / 每小时。移植产品原型 page.tsx 的 RateTable。
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { C } from '../constants';
import type { OpsKind } from '../types';
import { useFmt } from '../lib/useFmt';
import { I18N } from '../lib/useLabels';

/** 作业行。label / unit 是 i18n key（rate.rows.* / rate.units.*），unit 为 null 的行不统计袋数 */
export const RATE_ROWS: { key: string; kind: OpsKind; manual: 0 | 1 | null; unit: 'bigBag' | 'bag' | null }[] = [
    { key: 'bigbag', kind: 'bigbag', manual: null, unit: 'bigBag' },
    { key: 'in', kind: 'in', manual: null, unit: null },
    { key: 'bag0', kind: 'bag', manual: 0, unit: 'bag' },
    { key: 'bag1', kind: 'bag', manual: 1, unit: 'bag' },
    { key: 'dep', kind: 'dep', manual: null, unit: 'bag' },
    { key: 'load', kind: 'load', manual: null, unit: 'bag' },
    { key: 'out', kind: 'out', manual: null, unit: null }
];

const COL = '124px 150px 124px';
const HOURW = 112;

interface Props {
    /** 圈选区间内的小时（hub 当地时间 'YYYY-MM-DD HH'） */
    labels: string[];
    /** 作业类型|小时|人工 → 包裹数 / 袋数 */
    index: Map<string, { cnt: number; bags: number }>;
}

const RateTable: React.FC<Props> = ({ labels, index }) => {
    const { t } = useTranslation();
    const fmt = useFmt();
    const cellOf = (row: (typeof RATE_ROWS)[number], hour: string) => {
        const variants = row.manual === null ? [0, 1] : [row.manual];
        let cnt = 0;
        let bags = 0;
        for (const m of variants) {
            const c = index.get(`${row.kind}|${hour}|${m}`);
            if (c) {
                cnt += c.cnt;
                bags += c.bags;
            }
        }
        return { cnt, bags };
    };
    const hours = Math.max(1, labels.length);
    const stickyL: React.CSSProperties = { position: 'sticky', left: 0, background: '#fff', zIndex: 1 };
    const fmtCell = (cnt: number, bags: number, unit: string | null) => (
        <>
            <span style={{ color: C.text1 }}>{fmt.pcs(cnt)}</span>
            {unit && (
                <span className="ml-1.5" style={{ color: C.text3 }}>
                    {t(`${I18N}.rate.units.${unit}`, { n: fmt.num(bags) })}
                </span>
            )}
        </>
    );
    const pad = (v: number) => String(v).padStart(2, '0');

    return (
        <div className="mt-3">
            <div className="mb-1.5 text-[14px] font-semibold" style={{ color: C.text1 }}>
                {t(`${I18N}.rate.title`, { n: labels.length })}
                <span className="ml-2 text-[12.5px] font-normal" style={{ color: C.text3 }}>
                    {t(`${I18N}.rate.hint`)}
                </span>
            </div>
            <div className="overflow-x-auto">
                <div style={{ minWidth: 124 + 150 + 124 + labels.length * HOURW }}>
                    <div
                        className="grid text-[12.5px]"
                        style={{
                            gridTemplateColumns: `${COL} repeat(${labels.length}, ${HOURW}px)`,
                            color: C.text2,
                            background: C.headBg,
                            borderBottom: `1px solid ${C.border}`,
                            padding: '7px 6px'
                        }}
                    >
                        <div style={{ ...stickyL, background: C.headBg }}>{t(`${I18N}.rate.colKind`)}</div>
                        <div>{t(`${I18N}.rate.colTotal`)}</div>
                        <div>{t(`${I18N}.rate.colRate`)}</div>
                        {labels.map((l) => {
                            const h = Number(l.slice(11));
                            return (
                                <div key={l}>
                                    <div className="text-[11px]" style={{ color: C.text3 }}>
                                        {l.slice(5, 10)}
                                    </div>
                                    {pad(h)}:00–{pad((h + 1) % 24)}:00
                                </div>
                            );
                        })}
                    </div>
                    {RATE_ROWS.map((row) => {
                        const cells = labels.map((l) => cellOf(row, l));
                        const totCnt = cells.reduce((a, c) => a + c.cnt, 0);
                        const totBags = cells.reduce((a, c) => a + c.bags, 0);
                        return (
                            <div
                                key={row.key}
                                className="grid text-[13px]"
                                style={{
                                    gridTemplateColumns: `${COL} repeat(${labels.length}, ${HOURW}px)`,
                                    borderBottom: `1px solid ${C.borderLight}`,
                                    padding: '7px 6px'
                                }}
                            >
                                <div style={{ ...stickyL, color: C.text1, fontWeight: 500 }}>{t(`${I18N}.rate.rows.${row.key}`)}</div>
                                <div className="font-semibold">{fmtCell(totCnt, totBags, row.unit)}</div>
                                <div>{fmtCell(Math.round(totCnt / hours), Math.round(totBags / hours), row.unit)}</div>
                                {cells.map((c, i) => (
                                    <div key={labels[i]}>{fmtCell(c.cnt, c.bags, row.unit)}</div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default RateTable;
